import { useCallback, useEffect, useState } from "react";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";

import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { downloadExcel_KSK2010_R01, getKSK2010_R01AllCount, getKSK2010_R01List } from "./Api_KSK2010_R01";
import { defaultKSK2010_R01Dto, KSK2010_R01Dto } from "./Dto_KSK2010_R01";
import { KSK2010_R01Filter, useKSK2010_R01Store } from "./Store_KSK2010_R01";
import { AgGridReact } from "ag-grid-react";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useCommonCodeValidate } from "src/utils/commonCode/CommonCodeValidateContext";
import {
	buildCommonCodeRequirements,
	KSK2010_R01_ORDER_WORKPLACE_MESSAGE,
	KSK2010_R01_ROW_VALIDATE,
} from "src/utils/commonCode/commonCodeRequirementConfig";


interface OrderContentProps {
	gridRef: React.RefObject<AgGridReact<KSK2010_R01Dto> | null>;
	setMessage: (message: string) => void;  
}	


export const Content_KSK2010_R01 = (props: OrderContentProps) => {

	const { 
		gridRef,
		setMessage,
	} = props;

	const {
		KSK2010_R01Filter,
		setKSK2010_R01PastFilter,
		KSK2010_R01List, setKSK2010_R01List, 
		deletedKSK2010_R01List, removeDeletedKSK2010_R01List,
		selectedKSK2010_R01List, setSelectedKSK2010_R01List,
		setOriginalKSK2010_R01List,
		setAllCount,
	} = useKSK2010_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { compCdList, workPlaceList, jobTeamList, ordTypeList } = useCommonCodeStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();

	// 권한 정보 로드
	useFetchActionbarAuth("KSK2010_R01");

	const getParams = (filter: Partial<KSK2010_R01Filter>) => {
		return {
			compCd: filter.compCd,
			workplaceCd: filter.workplaceCd,
			jobTeam: filter.jobTeam,
			dateRange: filter.dateRange,
			closeTag: filter.closeTag,
		}
	}

	const renewalAllCount = useCallback(async () => {
		const response = await getKSK2010_R01AllCount(getParams(KSK2010_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [KSK2010_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (fetchType: 'search' | 'refetch') => {
		setLoadingSpinner(true);
	
		let initialListLength = KSK2010_R01List.length;
		// 저장 후 재조회 시에는 목록을 비우기.
		if (fetchType === 'refetch') {
			setKSK2010_R01List([]);
		}

		const responseData = await getKSK2010_R01List(
			'',
			getParams(KSK2010_R01Filter),
			fetchType === 'refetch' ? initialListLength : KSK2010_R01List.length ?? 0
		);
	
		let newList = [...responseData.data.data ] as KSK2010_R01Dto[];
		setKSK2010_R01List(newList);
	
		const processAllCount = await renewalAllCount();
		setOriginalKSK2010_R01List(newList);
		setSelectedKSK2010_R01List(undefined);
		removeDeletedKSK2010_R01List(deletedKSK2010_R01List);
		gridRef.current?.api.ensureIndexVisible(0, 'top');
	
		if (newList.length === 0) {
			showWarning('데이터가 없습니다.');
		} else {
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
		}
	
		setKSK2010_R01PastFilter(KSK2010_R01Filter);
		setLoadingSpinner(false);
	}, [
		KSK2010_R01Filter, KSK2010_R01List, deletedKSK2010_R01List,
		gridRef, setMessage, setLoadingSpinner,
		setOriginalKSK2010_R01List, setSelectedKSK2010_R01List, removeDeletedKSK2010_R01List,
		setKSK2010_R01List, setKSK2010_R01PastFilter, renewalAllCount,
	]);

	const fetchData = useCallback(async () => {
		await executeDataFetch('search');
	}, [executeDataFetch]);

	// 버튼 비활성화 상태 관리
	const [buttonDisabled, setButtonDisabled] = useState({
		fetch: false,
		create: false,
		delete: true,
		save: true,
		print: true,
		excel: true,
		init: false
	});

	useEffect(() => {
		setButtonDisabled(prev => ({
			...prev,
			delete: deletedKSK2010_R01List.length === 0 && selectedKSK2010_R01List === null,
			save: KSK2010_R01List.length === 0,
			print: KSK2010_R01List.length === 0,
			excel: KSK2010_R01List.length === 0
		}));
	}, [selectedKSK2010_R01List, deletedKSK2010_R01List, KSK2010_R01List]);
	
	const handleExcelClick = useCallback(async () => {
		const response = await downloadExcel_KSK2010_R01(
			'',
			getParams(KSK2010_R01Filter),
			KSK2010_R01List.length ?? 0
		);

		downloadSettingExcel({ responseData: response.data.data, fileName: '작업지시정보등록-excel.xlsx' });
	}, [KSK2010_R01Filter, KSK2010_R01List.length])
	
	const defaultKSK2010_R01Row = useCallback(() => {
		const workPlaceCdListToUse = workPlaceList.filter(
			opt => opt.useYn === 'Y' && opt.orderYn === 'Y',
		);

		const commonResult = validateCommonCodeLists([
			...buildCommonCodeRequirements(KSK2010_R01_ROW_VALIDATE, {
				compCdList,
				ordTypeList,
				workPlaceList,
				jobTeamList,
			}),
			{
				key: 'orderWorkPlaceList',
				list: workPlaceCdListToUse,
				message: KSK2010_R01_ORDER_WORKPLACE_MESSAGE,
			},
		]);

		if (!commonResult.valid) {
			return {
				...defaultKSK2010_R01Dto,
				inserted: true,
			};
		}

		return {
			...defaultKSK2010_R01Dto,
			compCd: compCdList[0].compCd,
			orderType: ordTypeList[0].minorCd,
			workplaceCd: workPlaceCdListToUse[0].workplaceCd,
			jobTeam: jobTeamList[0].minorCd,
			workProcess: workPlaceCdListToUse[0].workProcess,
			inserted: true,
		};
	}, [compCdList, ordTypeList, workPlaceList, jobTeamList, validateCommonCodeLists]);
	
	// const handleSaveClick = useCallback(async () => {
	// 	//조회 페이지 — postKSK2010_R01 / patchKSK2010_R01
	// }, []);
	
	// const handleDeleteClick = useCallback(() => {
	// 	//조회 페이지 — deleteKSK2010_R01
	// }, []);

	useEffect(() => {
		setOriginalKSK2010_R01List(JSON.parse(JSON.stringify(KSK2010_R01List)));
	}, [KSK2010_R01List, setOriginalKSK2010_R01List]);


	return{
		fetchData,
		defaultKSK2010_R01Row,
		// handleSaveClick,
		// handleDeleteClick,
		handleExcelClick,
		buttonDisabled,
	}
}
